import { useEffect, useState } from "react";
import { ArrowRight, Check, CircleHelp, ShieldCheck } from "lucide-react";

import {
  haalStart,
  vraagCode,
  verifieerCode,
  type AfstandContact,
  type AfstandStart,
} from "../../afstand/afstandApi";
import { readOpvangSleutel } from "../../afstand/afstandRoute";
import { AfstandAanvraag } from "./AfstandAanvraag";
import { AfstandMerk } from "./AfstandMerk";
import "./afstand.css";

/**
 * Het afstandsportaal: waar een eigenaar zijn dier aanmeldt bij het asiel.
 *
 * <p><b>Inloggen en aanmelden zijn dezelfde deur.</b> Je vult een e-mailadres in, krijgt een code en
 * bent binnen. Geen wachtwoord, geen registratie, geen keuze tussen "inloggen" en "account maken" --
 * dat zijn drie drempels voor iets wat de eigenaar niet wil doen maar moet doen. Pas ná de code
 * vraagt het portaal om naam, telefoon en adres.
 *
 * <p><b>Eén lopende aanvraag per adres.</b> Wie terugkomt met hetzelfde adres opent zijn eigen
 * aanvraag weer, met alle dieren erin. Twee losse aanvragen van dezelfde persoon zou het asiel
 * daarna aan elkaar moeten knopen.
 *
 * <p><b>Het asiel is herkenbaar aan logo en naam</b>, niet aan een eigen kleurenschema. Elk asiel
 * kan zijn hoofdkleur en beeld instellen; het patroon blijft overal hetzelfde.
 */
export function AfstandPortaal() {
  const opvangSleutel = readOpvangSleutel(globalThis.location?.pathname);

  const [start, setStart] = useState<AfstandStart | null>(null);
  // Twee heel verschillende dingen, die een bezoeker ook verschillend moet lezen: "deze opvang
  // kennen we niet" (dan klopt de link) en "het lukt nu even niet" (dan kan hij het straks opnieuw
  // proberen). Eén foutmelding laat hem in het eerste geval wachten op iets wat nooit komt.
  const [laadfout, setLaadfout] = useState<"onbekend" | "storing" | null>(null);

  const [stap, setStap] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [contact, setContact] = useState<AfstandContact | null>(null);

  useEffect(() => {
    let levend = true;
    haalStart(opvangSleutel)
      .then((s) => { if (levend) setStart(s); })
      .catch((e: { statusCode?: number }) => {
        if (levend) setLaadfout(e?.statusCode === 404 ? "onbekend" : "storing");
      });
    return () => { levend = false; };
  }, [opvangSleutel]);

  const verstuurCode = async () => {
    setFout(null);
    setBezig(true);
    try {
      setDevCode(await vraagCode(opvangSleutel, email.trim()));
      setCode("");
      setStap("code");
    } catch {
      // Een onbekend adres loopt hier gewoon door -- anders verraadt het antwoord wie er een
      // aanvraag heeft lopen. Alleen een echte storing meldt zich.
      setFout("Er ging iets mis bij het versturen. Probeer het zo nog eens.");
    } finally {
      setBezig(false);
    }
  };

  const controleerCode = async () => {
    setFout(null);
    setBezig(true);
    try {
      setToken(await verifieerCode(opvangSleutel, email.trim(), code.trim()));
    } catch {
      setFout("Deze code klopt niet of is verlopen. Vraag zo nodig een nieuwe aan.");
    } finally {
      setBezig(false);
    }
  };

  const naam = start?.opvangNaam ?? "";

  if (token) {
    return (
      <AfstandAanvraag
        start={start}
        opvangSleutel={opvangSleutel}
        token={token}
        email={email.trim()}
        contact={contact}
        onContact={setContact}
      />
    );
  }

  return (
    <div className="afst">
      <header className="afst__balk">
        <AfstandMerk opvangSleutel={opvangSleutel} naam={naam} />
        <div className="afst__balkrechts">
          <a className="afst__hulp" href="#hulp">
            <CircleHelp size={18} aria-hidden="true" /> Hulp nodig?
          </a>
          <span className="afst__wie">
            <span className="afst__wieicoon" aria-hidden="true"><ShieldCheck size={16} /></span>
            Uw aanvraag veilig starten
          </span>
        </div>
      </header>

      <main className="afst__blad">
        <section className="afst__uitleg">
          <p className="afst__eyebrow">Welkom in ons afstandsportaal</p>
          <h1>Dier aanmelden<br />voor afstand</h1>
          <p className="afst__lede">
            Via dit portaal kunt u een afstandsaanvraag starten voor één of meerdere dieren. U logt
            veilig in met een eenmalige code via e-mail.
          </p>
          <ul className="afst__punten">
            {[
              "Uw gegevens invullen wanneer het u uitkomt",
              "Meerdere dieren binnen één aanvraag",
              "De voortgang volgen zolang uw aanvraag actief is",
            ].map((belofte) => (
              <li key={belofte}>
                <span className="afst__vinkje" aria-hidden="true"><Check size={16} /></span>
                {belofte}
              </li>
            ))}
          </ul>

          {/* Beeld, geen gegeven: laadt het niet, dan sluit de kolom gewoon af onder de beloften. */}
          <AfstandHero />
        </section>

        <section className="afst__kaart">
          {laadfout === "onbekend" && (
            <>
              <p className="afst__eyebrow">Afstandsportaal</p>
              <h2>Deze link werkt niet</h2>
              <p className="afst__kaarttekst">
                We kunnen deze opvang niet vinden. Controleer de link op de website van het asiel, of
                neem telefonisch contact met ze op.
              </p>
            </>
          )}

          {laadfout === "storing" && (
            <>
              <p className="afst__eyebrow">Afstandsportaal</p>
              <h2>Het lukt nu even niet</h2>
              <p className="afst__kaarttekst">
                Dit portaal is op dit moment niet bereikbaar. Probeer het over een paar minuten nog
                eens; er is niets van u verloren gegaan.
              </p>
            </>
          )}

          {!laadfout && stap === "email" && (
            <>
              <p className="afst__eyebrow">Veilig inloggen zonder wachtwoord</p>
              <h2>Start uw afstandsaanvraag</h2>
              <p className="afst__kaarttekst">
                Vul uw e-mailadres in en ontvang een persoonlijke inlogcode. Er is geen wachtwoord
                nodig.
              </p>

              <label className="afst__veld">
                <span>E-mailadres</span>
                <span className="afst__invoer">
                  <input
                    type="email"
                    autoComplete="email"
                    placeholder="Bijv. j.devries@example.nl"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && email.trim()) void verstuurCode(); }}
                  />
                </span>
              </label>

              <button type="button" className="afst__knop" disabled={!email.trim() || bezig}
                      onClick={() => void verstuurCode()}>
                {bezig ? "Bezig…" : "Code versturen"}
                <ArrowRight size={18} aria-hidden="true" />
              </button>

              {fout && <p className="afst__melding afst__melding--fout" role="alert">{fout}</p>}

              <p className="afst__melding">
                Hebt u al een actieve aanvraag? Gebruik hetzelfde e-mailadres om verder te gaan. Na
                afhandeling wordt de toegang gesloten.
              </p>

              {/* Een opvang die nog niets heeft ingericht kan geen nieuwe aanvraag ontvangen.
                  Inloggen blijft kunnen: je komt hier net zo goed om een lopende aanvraag te zien. */}
              {start && !start.mogelijk && (
                <p className="afst__melding">
                  {naam || "Deze opvang"} neemt op dit moment geen nieuwe aanmeldingen aan via dit
                  portaal.
                </p>
              )}
            </>
          )}

          {!laadfout && stap === "code" && (
            <>
              <p className="afst__eyebrow">Veilig inloggen zonder wachtwoord</p>
              <h2>Voer uw inlogcode in</h2>
              <p className="afst__kaarttekst">
                Wij hebben een code gestuurd naar <strong>{email.trim()}</strong>. Vul deze hieronder
                in om verder te gaan.
              </p>

              <label className="afst__veld">
                <span>Uw code</span>
                <span className="afst__invoer">
                  <input
                    className="afst__code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="------"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    onKeyDown={(e) => { if (e.key === "Enter" && code.length === 6) void controleerCode(); }}
                  />
                </span>
              </label>

              <button type="button" className="afst__knop" disabled={code.length < 6 || bezig}
                      onClick={() => void controleerCode()}>
                {bezig ? "Bezig…" : "Inloggen"}
                <ArrowRight size={18} aria-hidden="true" />
              </button>

              {fout && <p className="afst__melding afst__melding--fout" role="alert">{fout}</p>}

              {/* Op test en acceptatie gaat er geen mail uit; dan staat de code hier, zodat er
                  getest kan worden zonder mailbox. In productie is dit altijd leeg. */}
              {devCode && (
                <p className="afst__melding">Testomgeving — uw code is <strong>{devCode}</strong></p>
              )}

              <p className="afst__melding">
                Geen code ontvangen? Controleer uw spam of vraag een korte wachttijd later een
                nieuwe code aan.
              </p>

              <button type="button" className="afst__terug"
                      onClick={() => { setStap("email"); setCode(""); setFout(null); }}>
                Ander e-mailadres gebruiken
              </button>
            </>
          )}
        </section>
      </main>
    </div>
  );
}

/** De hero bij de uitleg. Versiering: laadt hij niet, dan verdwijnt hij zonder gat achter te laten. */
function AfstandHero() {
  const [zichtbaar, setZichtbaar] = useState(true);
  if (!zichtbaar) {
    return null;
  }
  return (
    <figure className="afst__beeld">
      <img src="/afstand/hero.png" alt="" onError={() => setZichtbaar(false)} />
    </figure>
  );
}
